import React, {
  Dispatch,
  FC,
  FormEvent,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { Col, Input, Row } from 'reactstrap';
import {
  Department,
  VolunteerDepartmentModel,
  VolunteerUpsertModel,
} from '../../../../shared/volunteer';
import { MaterialIconText } from '../../../components/MaterialIconText';
import { useTranslation } from '../../../translations';
import { classNames } from '../../../utils';
import { FormActionType } from '../../form/FormReducer';

interface VolunteerDepartmentProps {
  readonly state: VolunteerUpsertModel;
  readonly dispatch: Dispatch<FormActionType>;
  readonly departments: Department[];
  readonly existingDepartments?: VolunteerDepartmentModel[];
}

export const NewVolunteerDepartments: FC<VolunteerDepartmentProps> = ({
  departments,
  state,
  existingDepartments,
  dispatch,
}) => {
  const [stateDepartments, setStateDepartments] = useState<Record<string, number[]>>({
    Avoid: state.departmentAvoid ?? [],
    Interest: state.departmentInterest ?? [],
    Experience: state.departmentExperience ?? [],
  });

  useEffect(() => {
    const {
      Avoid: departmentAvoid,
      Interest: departmentInterest,
      Experience: departmentExperience,
    } = stateDepartments;

    dispatch({
      type: 'updateVolunteerDepartment',
      departmentAvoid: departmentAvoid ?? [],
      departmentExperience: departmentExperience ?? [],
      departmentInterest: departmentInterest ?? [],
    });
  }, [dispatch, stateDepartments]);

  return (
    <Col>
      <VolunteerDepartments
        callback={setStateDepartments}
        departments={departments}
        volunteerDepartments={existingDepartments}
      />
    </Col>
  );
};

const departmentTypes = ['Experience', 'Interest', 'Avoid'] as const;

type DepartmentTypes = (typeof departmentTypes)[number];

const InterestHeader: FC<{ readonly children: ReactNode }> = ({ children }) => {
  return (
    <Col className="mb-10 text-center" lg={2} xs={4}>
      <strong>{children}</strong>
    </Col>
  );
};

interface InterestProps {
  Avoid: number[];
  Experience: number[];
  Interest: number[];
}

const VolunteerDepartments: FC<{
  readonly departments: Department[];
  readonly volunteerDepartments?: VolunteerDepartmentModel[];
  readonly callback?: Dispatch<SetStateAction<Record<string, number[]>>>;
}> = ({ departments, volunteerDepartments, callback }) => {
  const { ts } = useTranslation();
  const [state, setState] = useState<InterestProps>(() => {
    const initialState: InterestProps = {
      Avoid: [],
      Experience: [],
      Interest: [],
    };

    for (const depType of departmentTypes) {
      initialState[depType] =
        volunteerDepartments
          ?.filter((volDep) => (volDep.states as string[]).includes(depType.toLowerCase()))
          .map((volDep) => volDep.id) ?? [];
    }

    return initialState;
  });

  const changeSelection = useCallback(
    (type: DepartmentTypes, event: FormEvent<HTMLInputElement>) => {
      const value = Number.parseInt(event.currentTarget.value, 10);
      let newState: InterestProps;

      if (event.currentTarget.checked) {
        if (type === 'Interest' && state.Interest.length >= 5) {
          toast.error(ts('you_can_only_select_up_interests', '5'));
          return;
        }

        if (type === 'Avoid') {
          newState = {
            ...state,
            Avoid: [...state.Avoid, value],
            Experience: state.Experience.filter((n) => n !== value),
            Interest: state.Interest.filter((n) => n !== value),
          };
        } else {
          newState = {
            ...state,
            Avoid: state.Avoid.filter((n) => n !== value),
            [type]: [...state[type], value],
          };
        }
      } else {
        newState = {
          ...state,
          [type]: state[type].filter((n) => n !== value),
        };
      }

      setState(newState);
      if (callback) {
        callback({ ...newState });
      }
    },
    [callback, state, ts],
  );

  return (
    <Row className="department-select">
      <Col className="mb-10" lg={6} xs={12}>
        <strong>{ts('volunteerdepartments.department_name')}</strong>
      </Col>
      <InterestHeader>{ts('volunteer.form.experience')}</InterestHeader>
      <InterestHeader>{ts('volunteer.form.interest')}</InterestHeader>
      <InterestHeader>{ts('volunteer.form.avoid')}</InterestHeader>
      {departments.map(({ name, id, publiclyVisible }) => (
        <Col className="department-select-item" id={`department-${id}`} key={id} xs={12}>
          <Row>
            <Col className="mb-10" lg={6} xs={12}>
              {name}
              {!publiclyVisible && <HiddenDepartment />}
            </Col>
            {Object.keys(state).length > 0 &&
              departmentTypes.map((type) => {
                const formId = `department${type}${id}`;
                return (
                  <Col className="mb-10 text-center" key={type} lg={2} xs={4}>
                    <Input
                      checked={state[type].includes(id)}
                      className={classNames({
                        negative: type === 'Avoid',
                      })}
                      id={formId}
                      onChange={(event) => {
                        changeSelection(type, event);
                      }}
                      type="checkbox"
                      value={id}
                    />
                  </Col>
                );
              })}
            <Col className="mb-10 show-mobile" xs={12} />
          </Row>
        </Col>
      ))}
    </Row>
  );
};

export const HiddenDepartment: FC = () => {
  const { ts } = useTranslation();
  return (
    <div className="small text-muted">
      <MaterialIconText name="visibility" small>
        {ts('this_is_atshiddendepartment')}
      </MaterialIconText>
    </div>
  );
};
